import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup, DropdownMenuItem,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Extension} from "@/types/extenions.ts";
import React, {useEffect, useMemo, useState} from "react";
import {ConfirmProperties, extensionActions} from "@/pages/extensions/helpers.ts";
import {Action} from "@/pages/extensions/helpers.ts";
import {Spinner} from "@/components/ui/spinner.tsx";

export function ActionsExtension(props: {ext: Extension}) {

    const {ext} = props;

    const actions = useMemo(() => extensionActions.filterExt(ext), [ext.cls_type]);
    const [open, setOpen] = useState(false);

    if(actions.length === 0) {
        return null;
    }

    return (
        <DropdownMenu
            dir="rtl"
            modal={false}
            open={open}
            onOpenChange={setOpen}
        >
            <DropdownMenuTrigger asChild>
                <Button className="cursor-pointer rounded-full max-h-6 px-2.5" variant="outline" size="sm">
                    <span>פעולות</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-w-60">
                <DropdownMenuGroup>
                    {actions.map((action) => (
                        <ItemAction
                            key={action.action}
                            action={action}
                            ext={ext}
                            setOpen={setOpen}
                        />
                    ))}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export function ItemAction(props: {
    action: Action|Omit<Action, 'allow'|'types'>,
    ext: Extension
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
    const {action, ext, setOpen} = props;
    const [confirm, setConfirm] = useState<null|ConfirmProperties>(null);
    const [processing, setProcessing] = useState(false);

    const Comp = action.href ? 'a' : 'button';

    useEffect(() => {
        if(confirm) {
            setProcessing(false);
        }
    }, [confirm]);

    async function handleClick(e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
        if(action.href) {
            return;
        }

        if (action.confirm) {
            e.preventDefault();
            e.stopPropagation();
           setConfirm(action.confirm(ext));
        } else {
            await handleConfirmAction()
        }
    }

    async function handleConfirmAction() {
        setProcessing(true);
        await action.call(ext, () => {
            setConfirm(null);
            setOpen(false);
        })
        setProcessing(false);
    }

    return (
        <DropdownMenuItem
            disabled={action.disabled}
            asChild
            className="w-full"
            onClick={(e) => {
                if(confirm){
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                handleClick(e);
            }}
            {...(action.href ? {href: action.href(ext)} : {})}
        >
            <Comp>
                {confirm ? (
                    <div className="border bg-amber-50 border-amber-950/10 text-amber-950 rounded-xl p-2 px-3">
                        <div>{confirm.title}</div>
                        <div className="text-xs text-amber-950/60">{confirm.message}</div>
                        <div className="flex gap-2 justify-end mt-2">
                            <Button
                                size="xs"
                                variant="outline"
                                onClick={() => setConfirm(null)}
                            >
                                {confirm.cancelText}
                            </Button>

                            <Button
                                size="xs"
                                variant="destructive" onClick={handleConfirmAction}>
                                {processing && <Spinner size="sm"/>}
                                {confirm.confirmText}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex gap-2 w-full h-full text-start text-xs">
                        {action.icon && <action.icon className="!size-4 text-gray-400 transition"/>}
                        {action.name}
                    </div>
                )}
            </Comp>
        </DropdownMenuItem>
    )
}